// Errors thrown by code that is not ours — browser extensions, injected
// proxy/ISP scripts, and third-party XHR wrappers — that still reach our Sentry
// project because the throw happens inside a listener Sentry globally wrapped.
//
// These are unfixable from our side, so they get dropped at capture rather than
// left unresolved in Sentry. Each entry must key off a signature that OUR code
// cannot produce; a match that could also fire on a real Raidbots bug belongs in
// a narrower filter or not at all.
//
// Matching is message-based because beforeSend sees raw minified frames —
// function names are mangled and sourcemapped paths don't exist client-side.
// Same constraint as frontend/sentryFingerprints.js.

const SIGNATURES = [
  {
    // An injected script monkeypatches Node.appendChild and JSON.stringify's the
    // node React hands it, which walks into the React fiber's circular refs. The
    // `__reactFiber$` property in the cycle is the tell: our code never
    // stringifies DOM nodes, so a plain "circular structure" error without it is
    // still ours to fix. (FRONTEND-994)
    id: 'extension-json-stringify-dom-node',
    test: message => message.includes('Converting circular structure to JSON')
      && message.includes('__reactFiber$'),
  },
  {
    // A foreign XHR wrapper rejects with its request object instead of an Error.
    // `executorName` appears in neither our source nor any installed dependency.
    // (FRONTEND-99B)
    id: 'injected-xhr-object-rejection',
    test: message => message.includes('Object captured as promise rejection')
      && message.includes('executorName'),
  },
  {
    // An extension reads `responseText` on an XHR whose responseType is 'json',
    // which the DOM forbids. axios does read responseText for responseType
    // 'json', but we never set that in browser code (our only responseType is
    // arraybuffer, server-side), so the XHR is not ours. Revisit this entry if we
    // ever set responseType: 'json' on a browser request. (FRONTEND-8RG)
    id: 'foreign-xhr-response-type-read',
    test: message => message.includes("Failed to read the 'responseText' property from 'XMLHttpRequest'"),
  },
  {
    // Sentry suffixes fetch rejections with the target host. Our browser code
    // only calls fetch() same-origin (everything else goes through axios/XHR,
    // whose failures read "Network Error"), so a fetch to any other host is an
    // injected script's (motramby.com tracker etc). The caller-frame check in
    // beforeSend can't catch it: the wrapper frame it should skip is mangled
    // client-side. (FRONTEND-9AR/9A7/9A4)
    id: 'injected-fetch-foreign-host',
    test: (message) => {
      const host = message.match(/^Failed to fetch \(([^)]+)\)$/)?.[1];
      return Boolean(host) && !/(^|\.)raidbots\.com$/.test(host);
    },
  },
];

// The rejected value itself, rather than the message, identifies this one.
// Injected network shims reject with `{ details, message, status }` and a status
// of 0 (the request never went out). "Failed to send the request" appears in
// neither our source nor any installed dependency. (FRONTEND-8R2)
const isInjectedRequestRejection = originalException => (
  !!originalException
  && originalException.message === 'Failed to send the request'
  && originalException.status === 0
);

// True when the event came from code we do not control and cannot fix.
export const isForeignError = (exceptionValues, originalException) => {
  if (isInjectedRequestRejection(originalException)) {
    return true;
  }

  const messages = (exceptionValues || []).map(v => v?.value).filter(v => typeof v === 'string');

  return messages.some(message => SIGNATURES.some(sig => sig.test(message)));
};

export default isForeignError;
